import { useLang } from "../contexts/LangContext";
import { Link } from "react-router-dom";
import { LogoMark } from "./Logo";
import { useWhatsAppUrl } from "../lib/whatsapp";
import { CITIES } from "../data/cities";
import useConsent from "../hooks/useConsent";

export default function Footer() {
  const { t, lang } = useLang();
  const whatsappUrl = useWhatsAppUrl(lang);
  const { reset: resetCookieConsent } = useConsent();
  return (
    <footer id="site-footer" className="bg-[#090A0B] text-white grain mt-24">
      {/* Standorte (cities) cross-link bar — pure SEO + UX win */}
      <div className="border-b border-neutral-800/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-neutral-500 mb-3">Standorte</p>
          <div className="flex flex-wrap gap-2">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/standort/${c.slug}`}
                className="text-xs text-neutral-300 hover:text-[#FF5A00] border border-neutral-800 hover:border-[#FF5A00] px-3 py-1.5 transition"
                data-testid={`footer-city-${c.slug}`}
              >
                Talkani {c.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <LogoMark size={40} />
            <div className="flex flex-col leading-none">
              <span className="font-black tracking-tight text-xl">TALKANI<span className="text-[#FF5A00]">.</span></span>
              <span className="text-[10px] font-semibold tracking-widest text-neutral-500 uppercase mt-1">Logistik &amp; Reinigung GmbH i.&nbsp;Gr.</span>
            </div>
          </div>
          <p className="text-neutral-400 text-sm leading-relaxed max-w-md">
            Talkani — Logistik · Reinigung · Service. Lieferung, Transport, E-Scooter Service, Reinigung und Winterdienst in ganz Deutschland.
          </p>
        </div>
        <div>
          <h4 className="text-xs font-bold tracking-[0.2em] uppercase text-neutral-500 mb-4">Service</h4>
          <ul className="space-y-2 text-sm text-neutral-300">
            <li>{t.services.lieferung.name}</li>
            <li>{t.services.escooter_service.name}</li>
            <li>{t.services.umzug.name}</li>
            <li>{t.services.reinigung.name}</li>
            <li>{t.services.hausreinigung.name}</li>
            <li>{t.services.gartenpflege.name}</li>
            <li>{t.services.autoteile.name}</li>
            <li>{t.services.winterdienst.name}</li>
            <li>{t.services.plakatservice.name}</li>
            <li className="pt-3 mt-3 border-t border-neutral-800"><Link to="/loyalty" className="hover:text-[#FF5A00] transition font-bold" data-testid="footer-loyalty-link">★ Talkani Plus</Link></li>
            <li><Link to="/b2b" className="hover:text-[#FF5A00] transition font-bold" data-testid="footer-b2b-link">Business / B2B</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs font-bold tracking-[0.2em] uppercase text-neutral-500 mb-4">Kontakt</h4>
          <ul className="space-y-2 text-sm text-neutral-300">
            <li>Talkani Logistik &amp; Reinigung GmbH <span className="text-[#FF5A00]">i.&nbsp;Gr.</span></li>
            <li>Walddörferstraße 383</li>
            <li>22047 Hamburg</li>
            <li><a href="mailto:info@talkani.de" className="hover:text-white transition-colors">info@talkani.de</a></li>
            <li><a href="tel:+491758802646" className="hover:text-white transition-colors">+49 175 8802646</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-neutral-500 order-2 sm:order-1">© {new Date().getFullYear()} Talkani Logistik &amp; Reinigung GmbH i.&nbsp;Gr. {t.footer.rights}</p>

          <div className="flex items-center gap-3 order-1 sm:order-2" data-testid="footer-socials">
            <SocialLink href={whatsappUrl} label="WhatsApp" testid="social-whatsapp">
              <svg viewBox="0 0 32 32" className="w-4 h-4 fill-current" aria-hidden="true">
                <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.834 3.41 4.769 4.355.616.302 2.035.875 2.722.875.847 0 2.85-.875 3.196-1.677.171-.43.171-.802.071-.946-.114-.157-.215-.215-.487-.358-.473-.243-1.39-.686-1.892-.686zM24.476 7.587C22.293 5.388 19.272 4.157 16.16 4.157c-6.484 0-11.832 5.27-11.832 11.754 0 2.054.529 4.067 1.567 5.847L4 28.272l6.602-1.724a11.93 11.93 0 0 0 5.56 1.401h.014c6.484 0 11.832-5.275 11.832-11.76 0-3.108-1.358-6.115-3.532-8.601zM16.176 26.029h-.014c-1.84 0-3.65-.487-5.215-1.418l-.372-.215-3.86 1.017 1.03-3.762-.244-.387a9.804 9.804 0 0 1-1.503-5.232c0-5.42 4.4-9.805 9.805-9.805 2.605 0 5.062 1.017 6.906 2.865a9.71 9.71 0 0 1 2.85 6.94c-.014 5.404-4.414 9.804-9.819 9.804z"/>
              </svg>
            </SocialLink>

            <SocialLink href="https://www.linkedin.com/in/ali-al-talkani-ab28753a7" label="LinkedIn" testid="social-linkedin">
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
            </SocialLink>

            <SocialLink href="https://www.xing.com/profile/Ali_AlTalqani" label="XING" testid="social-xing">
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
                <path d="M18.188 0c-.517 0-.741.325-.927.66 0 0-7.455 13.224-7.702 13.657.015.024 4.919 9.023 4.919 9.023.17.308.436.66.967.66h3.454c.211 0 .375-.078.463-.22.089-.151.089-.346-.009-.536l-4.879-8.916c-.004-.006-.004-.016 0-.022L21.945.756c.095-.191.097-.387.006-.535C21.864.078 21.69 0 21.479 0h-3.291zM3.648 4.74c-.211 0-.385.074-.473.216-.09.149-.078.339.02.531l2.34 4.05c.004.01.004.016 0 .021L1.86 16.051c-.099.188-.093.381 0 .529.085.142.239.234.45.234h3.461c.518 0 .766-.348.945-.667l3.734-6.609-2.378-4.155c-.172-.315-.434-.659-.962-.659H3.648v.016z"/>
              </svg>
            </SocialLink>
          </div>

          <div className="flex gap-6 text-xs text-neutral-500 order-3">
            <a href="/blog" className="hover:text-white">{t.nav.blog}</a>
            <a href="/impressum" className="hover:text-white">{t.footer.imprint}</a>
            <a href="/datenschutz" className="hover:text-white">{t.footer.privacy}</a>
            <a href="/agb" className="hover:text-white">{t.footer.agb}</a>
            <button
              type="button"
              onClick={resetCookieConsent}
              className="hover:text-white cursor-pointer"
              data-testid="footer-cookie-settings"
            >
              {t.footer.cookies || "Cookies"}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

function SocialLink({ href, label, children, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={testid}
      className="w-9 h-9 rounded-sm border border-neutral-700 hover:border-[#FF5A00] hover:bg-[#FF5A00] hover:text-white flex items-center justify-center text-neutral-400 transition-colors"
    >
      {children}
    </a>
  );
}
